import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CoERoleDefinition } from '@/hooks/useCoEConfig';

const ICON_OPTIONS = ['Brain', 'Target', 'Shield', 'Users', 'Lightbulb', 'Briefcase', 'TrendingUp', 'Workflow', 'Building2', 'FileText', 'Scale'];
const COLOR_OPTIONS = [
  { value: 'bg-purple-500', label: 'Morado' },
  { value: 'bg-blue-500', label: 'Azul' },
  { value: 'bg-red-500', label: 'Rojo' },
  { value: 'bg-green-500', label: 'Verde' },
  { value: 'bg-yellow-500', label: 'Amarillo' },
  { value: 'bg-orange-500', label: 'Naranja' },
  { value: 'bg-pink-500', label: 'Rosa' },
  { value: 'bg-teal-500', label: 'Turquesa' },
  { value: 'bg-indigo-500', label: 'Índigo' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: CoERoleDefinition | null;
  onSave: (data: {
    title: string;
    seniority: string;
    icon_name: string;
    color: string;
    responsibilities: string[];
    skills: string[];
  }) => void;
}

export function CoERoleDialog({ open, onOpenChange, initial, onSave }: Props) {
  const [title, setTitle] = useState('');
  const [seniority, setSeniority] = useState('');
  const [iconName, setIconName] = useState('Users');
  const [color, setColor] = useState('bg-primary');
  const [responsibilities, setResponsibilities] = useState('');
  const [skills, setSkills] = useState('');

  useEffect(() => {
    if (open) {
      setTitle(initial?.title || '');
      setSeniority(initial?.seniority || '');
      setIconName(initial?.icon_name || 'Users');
      setColor(initial?.color || 'bg-primary');
      setResponsibilities((initial?.responsibilities || []).join('\n'));
      setSkills((initial?.skills || []).join('\n'));
    }
  }, [open, initial]);

  const handleSubmit = () => {
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      seniority: seniority.trim(),
      icon_name: iconName,
      color,
      responsibilities: responsibilities.split('\n').map(s => s.trim()).filter(Boolean),
      skills: skills.split('\n').map(s => s.trim()).filter(Boolean),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? 'Editar rol' : 'Agregar rol'}</DialogTitle>
          <DialogDescription>Define el rol clave del Centro de Excelencia IA para esta empresa.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Título *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ej. Líder de Datos" />
          </div>
          <div>
            <Label>Seniority</Label>
            <Input value={seniority} onChange={e => setSeniority(e.target.value)} placeholder="Ej. Director" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ícono</Label>
              <Select value={iconName} onValueChange={setIconName}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Color</Label>
              <Select value={color} onValueChange={setColor}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COLOR_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded ${c.value}`} />{c.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Responsabilidades (una por línea)</Label>
            <Textarea value={responsibilities} onChange={e => setResponsibilities(e.target.value)} rows={4} />
          </div>
          <div>
            <Label>Habilidades clave (una por línea)</Label>
            <Textarea value={skills} onChange={e => setSkills(e.target.value)} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={!title.trim()}>{initial ? 'Guardar' : 'Agregar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
